// src/pages/HolidayManagementPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Edit,
  Delete,
  MoreVert,
  Add,
  Search,
  CalendarToday,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  getHolidays,
  getHolidayYears,
  deleteHoliday,
  initializeYear,
  Holiday,
  HolidayApiError,
} from '../services/holidayApi';
import EditHolidayDialog from '../components/Holiday/EditHolidayDialog';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../services/authApi';

export function HolidayManagementPage() {
  const { user } = useAuth();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [holidayYears, setHolidayYears] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [_isLoadingYears, setIsLoadingYears] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Only System Admin can edit/delete holidays (not Exec Admin)
  const canEditHolidays = user && hasRole(user, 'System Admin');
  const canDeleteHolidays = user && hasRole(user, 'System Admin');
  
  // Filters
  const [yearFilter, setYearFilter] = useState<number | 'ALL'>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Menu and dialogs
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuHolidayId, setMenuHolidayId] = useState<number | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingHoliday, setDeletingHoliday] = useState<Holiday | null>(null);
  const [initializeYearDialogOpen, setInitializeYearDialogOpen] = useState(false);
  const [initializeYearValue, setInitializeYearValue] = useState<number>(new Date().getFullYear());
  const [isInitializing, setIsInitializing] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Load holidays
  const loadHolidays = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const filters: { year?: number } = {};
      if (yearFilter !== 'ALL' && typeof yearFilter === 'number') {
        filters.year = yearFilter;
      }
      
      const holidaysData = await getHolidays(filters);
      setHolidays(holidaysData);
    } catch (err) {
      const apiError = err as HolidayApiError;
      setError(apiError.message || 'Failed to load holidays');
      console.error('Error loading holidays:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load holiday years
  const loadHolidayYears = async () => {
    try {
      setIsLoadingYears(true);
      const years = await getHolidayYears();
      setHolidayYears(years);
    } catch (err) {
      console.error('Error loading holiday years:', err);
      // Don't show error for years - it's not critical
    } finally {
      setIsLoadingYears(false);
    }
  };

  useEffect(() => {
    loadHolidays();
    loadHolidayYears();
  }, [yearFilter]);

  // Filter holidays client-side by search query
  const filteredHolidays = useMemo(() => {
    let filtered = holidays;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(holiday =>
        holiday.holidayName.toLowerCase().includes(query) ||
        (holiday.description && holiday.description.toLowerCase().includes(query))
      );
    }
    
    // Sort by date (ascending)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.holidayDate + 'T00:00:00');
      const dateB = new Date(b.holidayDate + 'T00:00:00');
      return dateA.getTime() - dateB.getTime();
    });
  }, [holidays, searchQuery]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, holidayId: number) => {
    setMenuAnchor(event.currentTarget);
    setMenuHolidayId(holidayId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuHolidayId(null);
  };

  const handleCreate = () => {
    setEditingHoliday(null);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleEdit = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setEditingHoliday(null);
    setSuccessMessage('Holiday saved successfully');
    loadHolidays();
    loadHolidayYears(); // Refresh years in case new year was added
  };

  const handleDeleteClick = (holiday: Holiday) => {
    setDeletingHoliday(holiday);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!deletingHoliday) return;
    try {
      setActionLoading(deletingHoliday.holidayId);
      await deleteHoliday(deletingHoliday.holidayId);
      setDeleteDialogOpen(false);
      setDeletingHoliday(null);
      setSuccessMessage('Holiday deleted successfully');
      loadHolidays();
    } catch (err) {
      const apiError = err as HolidayApiError;
      setError(apiError.message || 'Failed to delete holiday');
      console.error('Error deleting holiday:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleInitializeYearClick = () => {
    setInitializeYearValue(new Date().getFullYear());
    setInitializeYearDialogOpen(true);
  };

  const handleInitializeYearConfirm = async () => {
    try {
      setIsInitializing(true);
      setError(null);
      await initializeYear(initializeYearValue);
      setInitializeYearDialogOpen(false);
      setSuccessMessage(`Standard holidays initialized for ${initializeYearValue}`);
      loadHolidays();
      loadHolidayYears();
    } catch (err) {
      const apiError = err as HolidayApiError;
      setError(apiError.message || 'Failed to initialize holidays');
      console.error('Error initializing holidays:', err);
    } finally {
      setIsInitializing(false);
    }
  };

  const formatHolidayDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  // Generate year options for filter (current year ± 5 years, plus any years with holidays)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = new Set<number>();
    
    // Add current year ± 5 years
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.add(i);
    }
    
    // Add all years that have holidays
    holidayYears.forEach(year => years.add(year));
    
    return Array.from(years).sort((a, b) => b - a); // Sort descending
  }, [holidayYears]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'white' }}>
          Holiday Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {canEditHolidays && (
            <Button
              variant="contained"
              startIcon={<CalendarToday />}
              onClick={handleInitializeYearClick}
            >
              Initialize Year
            </Button>
          )}
          {canEditHolidays && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
            >
              Add Holiday
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search by holiday name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 250, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={yearFilter}
              label="Year"
              onChange={(e) => {
                const value = e.target.value;
                setYearFilter(value === 'ALL' ? 'ALL' : (value as number));
              }}
            >
              <MenuItem value="ALL">All Years</MenuItem>
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Holidays Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Year</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Holiday Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHolidays.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery.trim() || yearFilter !== 'ALL'
                        ? 'No holidays found matching your filters'
                        : 'No holidays found. Click "Add Holiday" to create one or "Initialize Year" to add standard holidays.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredHolidays.map((holiday) => (
                  <TableRow key={holiday.holidayId}>
                    <TableCell>{holiday.year}</TableCell>
                    <TableCell>{formatHolidayDate(holiday.holidayDate)}</TableCell>
                    <TableCell>{holiday.holidayName}</TableCell>
                    <TableCell>{holiday.description || '-'}</TableCell>
                    <TableCell>
                      {holiday.isAutoAdded ? (
                        <Chip
                          label="Auto-Added"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="Manual"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, holiday.holidayId)}
                          disabled={actionLoading === holiday.holidayId}
                        >
                          <MoreVert />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuHolidayId && (() => {
          const holiday = holidays.find(h => h.holidayId === menuHolidayId);
          if (!holiday) return null;
          
          return (
            <>
              {canEditHolidays && (
                <MenuItem onClick={() => handleEdit(holiday)}>
                  <Edit sx={{ mr: 1 }} />
                  Edit Holiday
                </MenuItem>
              )}
              {canDeleteHolidays && (
                <MenuItem onClick={() => handleDeleteClick(holiday)}>
                  <Delete sx={{ mr: 1 }} />
                  Delete Holiday
                </MenuItem>
              )}
            </>
          );
        })()}
      </Menu>

      {/* Edit/Create Holiday Dialog */}
      <EditHolidayDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingHoliday(null);
        }}
        onSuccess={handleEditSuccess}
        holiday={editingHoliday}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Holiday</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deletingHoliday?.holidayName}</strong> ({deletingHoliday && formatHolidayDate(deletingHoliday.holidayDate)})?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={actionLoading !== null}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Initialize Year Dialog */}
      <Dialog open={initializeYearDialogOpen} onClose={() => setInitializeYearDialogOpen(false)}>
        <DialogTitle>Initialize Standard Holidays</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will create three standard holidays for the selected year:
            <ul>
              <li>New Year's Day (January 1)</li>
              <li>Independence Day (July 4)</li>
              <li>Christmas Day (December 25)</li>
            </ul>
            This action will only work if the year doesn't already have holidays defined.
          </DialogContentText>
          <TextField
            label="Year"
            type="number"
            value={initializeYearValue}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value)) {
                setInitializeYearValue(value);
              }
            }}
            fullWidth
            disabled={isInitializing}
            inputProps={{ min: 1900, max: 2100 }}
            helperText="Year (1900-2100)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInitializeYearDialogOpen(false)} disabled={isInitializing}>
            Cancel
          </Button>
          <Button
            onClick={handleInitializeYearConfirm}
            variant="contained"
            disabled={isInitializing}
          >
            {isInitializing ? 'Initializing...' : 'Initialize'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

